/**
 * SchedulerDashboard
 * ------------------
 * Primary admin landing page shown after authentication. Surfaces
 * scheduling stats, room health, and curated experiences for quick action.
 */
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import LinearProgress from '@mui/material/LinearProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';

import useApi from '../hooks/useApi';

const FALLBACK_STATS = [
  { label: 'Active Rage Rooms', value: '9', helper: 'across 4 launch cities' },
  { label: 'Sessions Today', value: '24', helper: '82% booked' },
  { label: 'VIP Holds', value: '5', helper: 'awaiting confirmations' },
];

const FALLBACK_ROOMS = [
  {
    id: 'vault',
    name: 'The Vault',
    city: 'Los Angeles',
    locationName: 'LA Arts District Hub',
    sessions: '10 slots',
    status: 'Operational',
    statusColor: 'success',
    fill: 82,
    note: 'Neon hammer kit ready.',
  },
  {
    id: 'neon-bay',
    name: 'Neon Bay',
    city: 'Miami',
    locationName: 'Wynwood Pop-Up',
    sessions: '8 slots',
    status: 'Sound Check',
    statusColor: 'warning',
    fill: 64,
    note: 'Awaiting DJ approval.',
  },
  {
    id: 'stage-b',
    name: 'Stage B',
    city: 'New York',
    locationName: 'Hudson Yards Annex',
    sessions: '12 slots',
    status: 'Maintenance',
    statusColor: 'error',
    fill: 0,
    note: 'Glass reset ETA 30 min.',
  },
];

const FALLBACK_SESSION_QUEUE = [
  { title: 'Executive Smash', time: '10:00 AM', location: 'Stage B', status: 'Boarding', statusColor: 'success' },
  { title: 'Creator Reset', time: '1:30 PM', location: 'Neon Bay', status: 'Soundcheck', statusColor: 'warning' },
  { title: 'All-Hands Detox', time: '4:15 PM', location: 'The Vault', status: 'Pending', statusColor: 'info' },
];

const DEFAULT_EXPERIENCES = [
  {
    title: 'Creative Ops',
    description: 'Plan pre-production reviews and approvals with clarity.',
  },
  {
    title: 'Live Activations',
    description: 'Track staging, load-in, and guest flow in real time.',
  },
  {
    title: 'Executive Sessions',
    description: 'Guarantee secure, distraction-free meeting blocks.',
  },
];

const DEFAULT_SCHEDULE_SOURCE_DATE = '2025-12-11';
const SCHEDULE_LOOKUP_DATE = (() => {
  const parsed = new Date(DEFAULT_SCHEDULE_SOURCE_DATE);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString().split('T')[0];
  }
  return parsed.toISOString().split('T')[0];
})();

const ROOM_STATUS_COLORS = {
  operational: 'success',
  'sound check': 'warning',
  soundcheck: 'warning',
  maintenance: 'error',
  offline: 'default',
  standby: 'info',
};

const SESSION_STATUS_COLORS = {
  boarding: 'success',
  confirmed: 'success',
  soundcheck: 'warning',
  pending: 'info',
  cancelled: 'error',
};

const resolveStatusColor = (map, status) => {
  const normalizedStatus = (status || '').toString().toLowerCase();
  return map[normalizedStatus] || 'default';
};

const percentFilled = (booked, total) => {
  if (!total) {
    return 0;
  }
  return Math.min(100, Math.round((booked / total) * 100));
};

const formatSessionTime = (value) => {
  if (!value) {
    return 'TBD';
  }
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }
  return parsedDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const unwrapApiRows = (response) => {
  if (!response) {
    return [];
  }
  if (Array.isArray(response)) {
    return response;
  }
  if (Array.isArray(response.rows)) {
    return response.rows;
  }
  if (Array.isArray(response.data?.rows)) {
    return response.data.rows;
  }
  if (Array.isArray(response.rooms)) {
    return response.rooms;
  }
  if (Array.isArray(response.data)) {
    return response.data;
  }
  if (Array.isArray(response.items)) {
    return response.items;
  }
  return [];
};

const SchedulerDashboard = () => {
  const navigate = useNavigate();
  const { rageRoom } = useApi();
  const rageRoomRef = useRef(rageRoom);

  useEffect(() => {
    rageRoomRef.current = rageRoom;
  }, [rageRoom]);

  const locationId = process.env.REACT_APP_DEFAULT_LOCATION_ID || 'default-location';

  const [rawRooms, setRawRooms] = useState([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [roomsError, setRoomsError] = useState(null);

  const [rawSessions, setRawSessions] = useState([]);
  const [sessionQueueLoading, setSessionQueueLoading] = useState(false);
  const [sessionQueueError, setSessionQueueError] = useState(null);

  const [availabilityRows, setAvailabilityRows] = useState([]);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const fetchRooms = async () => {
      if (!rageRoomRef.current?.admin?.listRooms) {
        setRoomsLoading(false);
        return;
      }

      setRoomsLoading(true);
      setRoomsError(null);

      try {
        const response = await rageRoomRef.current.admin.listRooms({ locationId });
        if (!isMounted) {
          return;
        }
        setRawRooms(unwrapApiRows(response));
      } catch (error) {
        if (isMounted) {
          setRoomsError(error.message || 'Unable to load rage rooms.');
          setRawRooms([]);
        }
      } finally {
        if (isMounted) {
          setRoomsLoading(false);
        }
      }
    };

    fetchRooms();

    return () => {
      isMounted = false;
    };
  }, [locationId]);

  useEffect(() => {
    if (!rawRooms.length) {
      setRawSessions((prev) => (prev.length ? [] : prev));
      return;
    }

    let isMounted = true;
    const primaryRoom = rawRooms[0];
    const roomId = primaryRoom?.id || primaryRoom?._id || primaryRoom?.roomId || primaryRoom?.slug;

    if (!roomId || !rageRoomRef.current?.admin?.listSchedule) {
      return;
    }

    const fetchSessionQueue = async () => {
      setSessionQueueLoading(true);
      setSessionQueueError(null);

      try {
        const response = await rageRoomRef.current.admin.listSchedule({
          locationId,
          roomId,
          query: {
            date: SCHEDULE_LOOKUP_DATE,
          },
        });

        if (!isMounted) {
          return;
        }

        setRawSessions(unwrapApiRows(response));
      } catch (error) {
        if (isMounted) {
          setSessionQueueError(error.message || 'Unable to load session queue.');
          setRawSessions([]);
        }
      } finally {
        if (isMounted) {
          setSessionQueueLoading(false);
        }
      }
    };

    fetchSessionQueue();

    return () => {
      isMounted = false;
    };
  }, [locationId, rawRooms]);

  useEffect(() => {
    let isMounted = true;

    if (!rawRooms.length || !rageRoomRef.current?.public?.listAvailability) {
      setAvailabilityRows([]);
      setAvailabilityError(null);
      setAvailabilityLoading(false);
      return () => {
        isMounted = false;
      };
    }

    const fetchAvailability = async () => {
      setAvailabilityLoading(true);
      setAvailabilityError(null);

      try {
        const response = await rageRoomRef.current.public.listAvailability({
          locationId,
          startDate: SCHEDULE_LOOKUP_DATE,
          endDate: SCHEDULE_LOOKUP_DATE,
          onlyAvailable: false,
          pageSize: 500,
        });

        if (!isMounted) {
          return;
        }

        setAvailabilityRows(unwrapApiRows(response));
      } catch (error) {
        if (isMounted) {
          setAvailabilityError(error.message || 'Unable to load availability data.');
          setAvailabilityRows([]);
        }
      } finally {
        if (isMounted) {
          setAvailabilityLoading(false);
        }
      }
    };

    fetchAvailability();

    return () => {
      isMounted = false;
    };
  }, [locationId, rawRooms]);

  const availabilityByRoom = useMemo(() => {
    if (!availabilityRows.length) {
      return {};
    }

    return availabilityRows.reduce((acc, row) => {
      const roomId =
        row.roomId ||
        row.room?._id ||
        row.room?.id ||
        row.room?.roomId ||
        row.room?.slug ||
        row.room?.name;

      if (!roomId) {
        return acc;
      }

      const totalCapacity = Number(row.maxCapacity) || 0;
      const remainingCapacity = row.isBlocked ? 0 : Math.max(Number(row.remainingCapacity) || 0, 0);

      if (!acc[roomId]) {
        acc[roomId] = {
          totalCapacity: 0,
          remainingCapacity: 0,
          totalSlots: 0,
        };
      }

      acc[roomId].totalCapacity += totalCapacity;
      acc[roomId].remainingCapacity += remainingCapacity;
      acc[roomId].totalSlots += 1;

      return acc;
    }, {});
  }, [availabilityRows]);

  const stats = useMemo(() => {
    if (!rawRooms.length) {
      return FALLBACK_STATS;
    }

    const uniqueCities = new Set(
      rawRooms.map((room) => room.city || room.location?.city || room.locationName).filter(Boolean)
    );

    const totalSlots = rawRooms.reduce(
      (acc, room) =>
        acc +
        Number(
          room.schedule?.totalSlots ??
            room.sessionsTotal ??
            room.dailyCapacity ??
            room.capacity?.total ??
            0
        ),
      0
    );

    const bookedSlots = rawRooms.reduce(
      (acc, room) =>
        acc +
        Number(room.schedule?.bookedSlots ?? room.sessionsBooked ?? room.capacity?.booked ?? 0),
      0
    );

    const utilization = totalSlots ? Math.round((bookedSlots / totalSlots) * 100) : 0;

    const activeRooms =
      rawRooms.filter(
        (room) => room.isActive !== false && (room.status || '').toLowerCase() !== 'maintenance'
      ).length || rawRooms.length;

    return [
      {
        label: 'Active Rage Rooms',
        value: String(activeRooms),
        helper: uniqueCities.size
          ? `across ${uniqueCities.size} launch cities`
          : 'across active launch cities',
      },
      {
        label: 'Sessions Today',
        value: String(totalSlots),
        helper: totalSlots ? `${utilization}% booked` : 'syncing schedule',
      },
    ];
  }, [rawRooms]);

  const roomsToDisplay = useMemo(() => {
    if (!rawRooms.length) {
      return FALLBACK_ROOMS.map((room) => ({
        ...room,
        locationName: room.locationName || room.city || 'Unknown Location',
        isEditable: false,
        capacityLabel: `${room.fill}% capacity locked`,
        isFullyBooked: room.fill >= 100,
      }));
    }

    return rawRooms.map((roomData) => {
      const totalSlots = Number(
        roomData.schedule?.totalSlots ??
          roomData.sessionsTotal ??
          roomData.dailyCapacity ??
          roomData.capacity?.total ??
          roomData.sessions?.length ??
          0
      );
      const bookedSlots = Number(
        roomData.schedule?.bookedSlots ??
          roomData.sessionsBooked ??
          roomData.capacity?.booked ??
          roomData.sessions?.filter?.((session) => session.isBooked)?.length ??
          0
      );

      const status =
        roomData.status ||
        roomData.operationalStatus ||
        (roomData.isActive === false ? 'Offline' : 'Operational');

      const roomId = roomData.id || roomData._id || roomData.roomId || roomData.slug || roomData.name;

      const locationName =
        roomData.locationName ||
        roomData.location?.name ||
        roomData.location?.label ||
        roomData.location?.title ||
        roomData.location?.displayName ||
        roomData.city ||
        roomData.location?.city ||
        'Unknown Location';

      const availability = availabilityByRoom[roomId];
      let fill = percentFilled(bookedSlots, totalSlots);
      let capacityLabel = `${fill}% capacity locked`;
      let isFullyBooked = false;

      if (availability) {
        const totalCapacity = availability.totalCapacity || totalSlots;
        const usedCapacity = Math.max(totalCapacity - availability.remainingCapacity, 0);
        fill = percentFilled(usedCapacity, totalCapacity || totalSlots);
        if (availability.remainingCapacity <= 0) {
          capacityLabel = 'Fully booked';
          isFullyBooked = true;
        } else {
          const slotsWord = availability.remainingCapacity === 1 ? 'slot' : 'slots';
          capacityLabel = `${availability.remainingCapacity} ${slotsWord} open`;
        }
      }

      return {
        id: roomId,
        name: roomData.name || roomData.displayName || 'Unnamed Room',
        city: roomData.city || roomData.location?.city || roomData.locationName || 'Unknown City',
        locationName,
        locationAddress: roomData.locationAddress,
        sessions: totalSlots ? `${totalSlots} slots` : 'Schedule pending',
        status,
        statusColor: resolveStatusColor(ROOM_STATUS_COLORS, status),
        fill,
        note: roomData.operatorNote || roomData.note || 'No operator notes yet.',
        sourceRoom: roomData,
        isEditable: Boolean(roomId),
        capacityLabel,
        isFullyBooked,
      };
    });
  }, [availabilityByRoom, rawRooms]);

  const sessionQueueItems = useMemo(() => {
    if (!rawSessions.length) {
      return FALLBACK_SESSION_QUEUE;
    }

    return rawSessions.map((session) => {
      const status = session.status || session.state || 'Pending';
      return {
        id: session.id || session.sessionId || `${session.experienceName}-${session.startTime}`,
        title: session.experienceName || session.title || session.name || 'Untitled Session',
        time: formatSessionTime(session.startTime || session.time),
        location: session.roomName || session.room?.name || session.location || 'Unknown Room',
        status,
        statusColor: resolveStatusColor(SESSION_STATUS_COLORS, status),
      };
    });
  }, [rawSessions]);

  const experiences = useMemo(() => DEFAULT_EXPERIENCES, []);

  const handleRoomNavigation = useCallback(
    (room) => {
      if (!room?.isEditable || !room?.id) {
        return;
      }
      navigate(`/admin/rooms/${room.id}/edit`, {
        state: room.sourceRoom ? { room: room.sourceRoom } : undefined,
      });
    },
    [navigate]
  );

  const handleRoomKeyDown = useCallback(
    (event, room) => {
      if (!room?.isEditable) {
        return;
      }
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleRoomNavigation(room);
      }
    },
    [handleRoomNavigation]
  );

  return (
    <Stack spacing={6}>
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={4}
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Box>
          <Chip
            label="Authenticated • Control Center"
            color="primary"
            sx={{
              mb: 2,
              fontSize: 13,
              fontWeight: 600,
              backgroundColor: 'rgba(60,31,255,0.18)',
              border: '1px solid rgba(244,243,248,0.16)',
            }}
          />
          <Typography variant="h3" gutterBottom>
            Rage Room Scheduler
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 520 }}>
            Review live capacity, approve VIP requests, and push playlist or scent changes without leaving the dashboard.
            All rooms stay synced with the public booking API.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button variant="contained" color="primary" onClick={() => navigate('/admin/rooms/new')}>
            Add Rage Room
          </Button>
          <Button variant="outlined" color="secondary" onClick={() => navigate('/admin/schedules/import')}>
            Import Schedule
          </Button>
          <Button variant="outlined" color="inherit" onClick={() => navigate('/admin/schedules/edit')}>
            Edit Schedules
          </Button>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {stats.map((stat) => (
          <Grid item xs={12} md={4} key={stat.label}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="overline" color="text.secondary">
                  {stat.label}
                </Typography>
                <Typography variant="h4" sx={{ my: 1 }}>
                  {stat.value}
                </Typography>
                <Typography color="text.secondary">{stat.helper}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Stack spacing={3}>
        <Typography variant="h5">Managed Rage Rooms</Typography>
        {roomsError && (
          <Typography color="error" variant="body2">
            {roomsError}
          </Typography>
        )}
        {availabilityError && (
          <Typography color="error" variant="body2">
            {availabilityError}
          </Typography>
        )}
        <Grid container spacing={3}>
          {roomsLoading || availabilityLoading ? (
            <Grid item xs={12}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Loading rage rooms...
                  </Typography>
                  <LinearProgress />
                </CardContent>
              </Card>
            </Grid>
          ) : (
            roomsToDisplay.map((room) => (
              <Grid item xs={12} md={4} key={room.id}>
                <Card
                  sx={{
                    height: '100%',
                    cursor: room.isEditable ? 'pointer' : 'default',
                    transition: room.isEditable ? 'box-shadow 0.2s ease' : 'none',
                  }}
                  onClick={() => handleRoomNavigation(room)}
                  onKeyDown={(event) => handleRoomKeyDown(event, room)}
                  role={room.isEditable ? 'button' : undefined}
                  tabIndex={room.isEditable ? 0 : -1}
                >
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="h6">{room.name}</Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip label={room.status} color={room.statusColor} size="small" />
                        {room.isEditable && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleRoomNavigation(room);
                            }}
                          >
                            Edit
                          </Button>
                        )}
                      </Stack>
                    </Stack>
                    <Typography color="text.secondary">{room.locationName}</Typography>
                    <Typography color="text.secondary">{room.locationAddress}</Typography>
                    <Typography color="text.secondary" sx={{ mb: 1 }}>
                      {room.city} • {room.sessions}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={room.fill}
                      sx={{ height: 8, borderRadius: 999, mb: 1 }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ color: room.isFullyBooked ? 'error.main' : 'text.secondary' }}
                    >
                      {room.capacityLabel}
                    </Typography>
                    {/*
                    <Divider sx={{ my: 2, borderColor: 'rgba(244,243,248,0.08)' }} />
                    <Typography variant="body2">{room.note}</Typography>
                    */}
                  </CardContent>
                </Card>
              </Grid>
            ))
          )}
        </Grid>
      </Stack>
    </Stack>
  );
};

export default SchedulerDashboard;